import { useQuery } from '@apollo/client';
import { APIErrorCodes } from 'app-types';
import { Notification } from 'Components';
import { usePrevious } from 'Hooks/usePrevious';
import { useStores } from 'Hooks/useStore';
import { isEqual } from 'lodash';
import { observer } from 'mobx-react-lite';
import { AddUserValues, EditUserValues, PrimarySystemRole } from 'Pages/User/User.types';
import { useMemo, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GET_JOB_CLASSES, GET_USER_ROLES } from 'Services/Api/Masterdata/Queries';
import { GetJobClassesResponse, GetUserRolesResponse } from 'Services/Api/Masterdata/Types';
import { getIsModuleSettingsDisabled } from './ModuleSettings.helpers';
import { SystemRoleType } from 'Services/Api/Roles/Types';
import { Group } from 'Services/Api/Groups/Types';
import { useFormikContext } from 'formik';
import { getCustomTenantRoles, getUserTenantRoles } from 'Pages/User/service/index.Service';
import { toCustomTenantRoleOptionKey } from 'Pages/User/helpers/tenantRoleAssignment';
import { useDashboardContext } from 'Pages/Dashboard/DashboardContext';

interface ModuleSettingsContainerProps {
	values: Pick<EditUserValues, 'primarySystemRoleId' | 'groups'>;
	onDisabled: () => void;
	children: (disabled: boolean) => React.ReactNode;
}

export function useIsModuleSettingDisabled(
	values?: Pick<EditUserValues, 'primarySystemRoleId' | 'groups'>
) {
	const { authStore } = useStores();
	const disabled = useMemo(() => {
		return values
			? getIsModuleSettingsDisabled(
				values,
				authStore.currentUserPermissionCodes
			)
			: true;
	}, [values, authStore.currentUserPermissionCodes]);

	const getIsLMSModuleSettingsDisabled = (
		values: Pick<EditUserValues, 'primarySystemRoleId' | 'groups'>
	) => {
		return getIsModuleSettingsDisabled(
			values,
			authStore.currentUserPermissionCodes
		);
	};
	return { disabled, getIsLMSModuleSettingsDisabled };
}

interface UseJobClassProps {
	values: Pick<EditUserValues, 'groups' | 'moduleSettings'> & {
		primarySystemRoleId?:
		| EditUserValues['primarySystemRoleId']
		| PrimarySystemRole;
	};
}
export function useJobClass({ values }: UseJobClassProps) {
	const { setFieldValue } = useFormikContext();
	const shouldSkipRequest = useMemo(() => {
		if (Array.isArray(values?.primarySystemRoleId)) {
			const systemRole = values.primarySystemRoleId.find(
				(role) => role.data?.isPrimary
			);
			const role = systemRole?.data?.role;
			if (
				role === SystemRoleType.SuperAdmin ||
				role === SystemRoleType.Owner
			) {
				return true;
			}
		}
		if (!Array.isArray(values?.groups) || !values.groups.length) return true;
		return false;
	}, [values?.primarySystemRoleId, values?.groups]);
	const { t } = useTranslation();
	const groupIds = useMemo(() => (values.groups ?? []).map((d) => d.key), [
		values.groups
	]);
	const { called, loading, data, refetch } = useQuery<GetJobClassesResponse>(
		GET_JOB_CLASSES,
		{
			fetchPolicy: 'cache-first',
			skip: shouldSkipRequest,
			variables: {
				groupIds
			}
		}
	);

	const fetch = async () => {
		try {
			if (shouldSkipRequest) return;
			await refetch({
				variables: {
					groupIds
				}
			});
		} catch (e) {
			if (groupIds.length) {
				Notification.error({
					description: t(
						`user.errorCodes.${APIErrorCodes.UserGroupInTheSameHierarchy}`
					)
				});
			} else {
				Notification.error({
					description: t('errorCodes.genericErrorMessage')
				});
			}
		}
	};

	useEffect(() => {
		fetch();
		// eslint-disable-next-line
	}, [groupIds]);

	useEffect(() => {
		if (shouldSkipRequest) return;

		if (!data?.getJobClassesList?.length) {
			setFieldValue('moduleSettings.jobClassId', '0');
			return;
		}
		const currentId = values.moduleSettings?.jobClassId;
		const exists = data.getJobClassesList.some(
			(item) => item.peakV3Id === currentId
		);

		if (currentId && currentId !== '0' && exists) {
			return;
		}
		const defaultJobClassId = data.getJobClassesList[0]?.peakV3Id || '0';
		setFieldValue('moduleSettings.jobClassId', defaultJobClassId);
	}, [
		data?.getJobClassesList,
		setFieldValue,
		values.moduleSettings?.jobClassId,
		called,
		loading
	]);

	return {
		loading: shouldSkipRequest ? false : loading,
		data: (data?.getJobClassesList && data.getJobClassesList.length > 0)
			? data.getJobClassesList
			: [{ id: 0, name: 'Default', peakV3Id: '0' }]
	};
}

interface UseResetLmsFieldsOnGroupsChangeProps {
	values: Pick<EditUserValues, 'groups' | 'moduleSettings'>;
	resetLmsFields: () => void;
}
export const useResetLmsFieldsOnGroupsChange = ({
	values,
	resetLmsFields
}: UseResetLmsFieldsOnGroupsChangeProps) => {
	const previousValues = usePrevious(values);
	useEffect(() => {
		const isModuleSettingsChanged = !isEqual(
			previousValues?.moduleSettings,
			values.moduleSettings
		);
		if (
			previousValues?.groups &&
			!isEqual(values.groups, previousValues.groups) &&
			!isModuleSettingsChanged
		) {
			resetLmsFields();
		}
		// eslint-disable-next-line
	}, [
		values.groups,
		values.moduleSettings,
		previousValues?.groups,
		previousValues?.moduleSettings
	]);
};

export const useUserTenantRoles = () => {
	const formik = useFormikContext<EditUserValues>();
	const hasFetched = useRef(false);
	const valuesRef = useRef(formik.values);
	valuesRef.current = formik.values;

	useEffect(() => {
		const peakV3Id = formik.values?.peakV3Id;
		if (!peakV3Id || hasFetched.current) return;
		const { resetForm } = formik;

		const fetch = async () => {
			try {
				hasFetched.current = true;

				const data = await getUserTenantRoles(peakV3Id);
				const latest = valuesRef.current;
				const selectedGroupId = latest.groups?.[0]?.key;
				const existingRoles = latest.primarySystemRoleId ?? [];

				const tenantRoles = (data.tenantRoles ?? []).map((role: any) => {
					const key = toCustomTenantRoleOptionKey(role.roleId);
					return {
						key,
						value: key,
						label: role.roleName,
						rootGroupId: role.tenantId,
						selectedGroupId,
						data: {
							id: role.roleId,
							role: role.roleName,
							isPrimary: false,
							isCustomTenantRole: true,
							tenantId: role.tenantId,
							moduleSettings: role.moduleSettings
						}
					};
				});

				const systemRolesOnly = existingRoles.filter(
					(role) => !role.data?.isCustomTenantRole
				);

				resetForm({
					values: {
						...latest,
						primarySystemRoleId: [...systemRolesOnly, ...tenantRoles]
					}
				});
			} catch (e) {
				hasFetched.current = false;
				console.error(e);
			}
		};

		fetch();
	}, [formik.values?.peakV3Id]);
};

export const useCustomTenantRolesDropdownList = (
	groups: any
) => {
	const [
		customTenantRolesDropdownList,
		setCustomTenantRolesDropdownList
	] = useState<Group[]>([]);
	const [loading, setLoading] = useState(false);
	const previousGroupId = useRef(groups?.[0]?.key);
	const { setFieldValue } =
		useFormikContext<AddUserValues>();
	const { setToastInfo } = useDashboardContext();
	const { t } = useTranslation();

	const groupId = groups?.[0]?.key;

	useEffect(() => {
		const fetchRoles = async () => {
			if (groupId == null) {
				setCustomTenantRolesDropdownList([]);
				setLoading(false);
				return;
			}

			if (previousGroupId.current !== groupId) {
				setFieldValue('customTenantRoleIds', []);
				previousGroupId.current = groupId;
			}

			try {
				setLoading(true);
				const data = await getCustomTenantRoles(groupId);
				setCustomTenantRolesDropdownList(
					(data.data || []).map((role: any) => ({
						id: role.id,
						name: role.roleName,
						description: role.description,
						rootGroupId: role.tenantId ?? groupId
					}))
				);
			} catch {
				setCustomTenantRolesDropdownList([]);
			} finally {
				setLoading(false);
			}
		};

		fetchRoles();
	}, [groupId, setFieldValue]);

	return { customTenantRolesDropdownList, loading };
};

export function useUserRole({ values }: UseJobClassProps) {
    const { setFieldValue } = useFormikContext();
    const shouldSkipRequest = useMemo(() => {
        if (Array.isArray(values?.primarySystemRoleId)) {
            const systemRole = values.primarySystemRoleId.find(
                (role) => role.data?.isPrimary
            );
            const role = systemRole?.data?.role;
            if (
                role === SystemRoleType.SuperAdmin ||
                role === SystemRoleType.Owner
            ) {
                return true;
            }
        }
        return false;
    }, [values?.primarySystemRoleId, values?.groups]);

 const { loading, data } = useQuery<GetUserRolesResponse>(
        GET_USER_ROLES,
        {
            fetchPolicy: 'cache-first',
            skip: shouldSkipRequest
        }
    );

 useEffect(() => {
        // If a value already exists, or data still loading — do nothing
        if (values.moduleSettings?.userRoleId || loading) {
            return;
        }

        const roles = (data?.getUserRolesList && data.getUserRolesList.length > 0)
            ? data.getUserRolesList
            : [{ id: 2, role: 'admin' }];
        // Prefer "admin"; accept common variants too, then fall back to first non-superadmin role
        const adminRole = roles.find((r) => {
            const name = r.role?.toLowerCase?.() || '';
            return name === 'admin' || name === 'administrator';
        }) || roles.find((r) => r.role?.toLowerCase() !== 'superadmin') || roles[0];

        if (adminRole) {
            setFieldValue('moduleSettings.userRoleId', adminRole.id);
        } else {
            setFieldValue('moduleSettings.userRoleId', 2);
        }
    }, [
        data?.getUserRolesList,
        setFieldValue,
        values.moduleSettings?.userRoleId,
        loading,
        shouldSkipRequest
    ]);

    return {
        loading: shouldSkipRequest ? false : loading,
        data: (data?.getUserRolesList && data.getUserRolesList.length > 0)
            ? data.getUserRolesList
            : [{ id: 2, role: 'admin' }]
    };
}

function ModuleSettingsContainer({
	values,
	onDisabled,
	children
}: ModuleSettingsContainerProps) {
	const { disabled } = useIsModuleSettingDisabled(values);
	const previousDisabled = usePrevious(disabled);

	useEffect(() => {
		if (previousDisabled === false && disabled) {
			onDisabled();
		}
	}, [previousDisabled, disabled, onDisabled]);

	return <>{children(disabled)}</>;
}

export default observer(ModuleSettingsContainer);